import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
	Box,
	Button,
	Container,
	Flex,
	Heading,
	Text,
	SimpleGrid,
	VStack,
	HStack,
	Icon,
	Avatar,
	Badge,
	Link,
	Card,
	CardBody,
	useColorModeValue,
	Img,
	IconButton,
	Drawer,
	DrawerBody,
	DrawerHeader,
	DrawerOverlay,
	DrawerContent,
	DrawerCloseButton,
	useDisclosure,
	Divider,
} from '@chakra-ui/react';
import {
	motion,
	useInView,
	useScroll,
	useTransform,
	useSpring,
	AnimatePresence,
} from 'framer-motion';
import {
	FiArrowRight,
	FiCheckCircle,
	FiPlay,
	FiStar,
	FiChevronLeft,
	FiChevronRight,
	FiMenu,
} from 'react-icons/fi';
import OpPilotCRMLogo from '../../../assets/logoonlyonwhitebg.png';
import HomePageHeroPreview from './HomePageHeroPreview';
import HomePageBlogsmithSpotlight from './HomePageBlogsmithSpotlight';
import {
	MoneyBackGuaranteeStrip,
	MoneyBackGuaranteeBadge,
} from './HomePageGuaranteeBanner';
import TrustLogoMarquee from './TrustLogoMarquee';
import RotatingWord from './RotatingWord';
import StackComparisonStrip from './StackComparisonStrip';
import HomePageBlogsmithFaq from './HomePageBlogsmithFaq';
import AuroraBackground from './AuroraBackground';
import FilmGrain from './FilmGrain';
import FloatingParticles from './FloatingParticles';
import ScrollProgressBar from './ScrollProgressBar';
import SectionCurve from './SectionCurve';
import NavSectionLink from './NavSectionLink';
import HomePagePricingSection from './HomePagePricingSection';
import { PILLAR_GLOW, HERO_BG_BASE } from './homePageVisuals';
import { keyframes } from '@emotion/react';
import {
	NAV_LINKS,
	HERO_STATS,
	HERO_ROTATING_WORDS,
	PLATFORM_PILLARS,
	CAPABILITY_GRID,
	WORKFLOW_STEPS,
	TESTIMONIALS,
	HERO_TRUST_POINTS,
	MONEY_BACK_GUARANTEE,
} from './homePageContent';
import Reveal from './Reveal';
import Footer from '../../../components/Reusables/FooterSection/Footer';

const MotionBox = motion.create(Box);
const MotionFlex = motion.create(Flex);
const MotionHeading = motion.create(Heading);
const MotionText = motion.create(Text);

const btnShine = keyframes`
  0% { transform: translateX(-120%) skewX(-18deg); }
  100% { transform: translateX(220%) skewX(-18deg); }
`;

const primaryCtaSx = {
	position: 'relative',
	overflow: 'hidden',
	_before: {
		content: '""',
		position: 'absolute',
		top: 0,
		bottom: 0,
		left: 0,
		w: '45%',
		bg: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.35), transparent)',
		transform: 'translateX(-120%) skewX(-18deg)',
		pointerEvents: 'none',
	},
	_hover: {
		_before: { animation: `${btnShine} 0.65s ease forwards` },
	},
};

const AnimatedCounter = ({ value, suffix = '' }) => {
	const ref = useRef(null);
	const inView = useInView(ref, { once: true });
	const [count, setCount] = useState(0);

	useEffect(() => {
		if (!inView) return;
		let start = 0;
		const end = value;
		const duration = 1400;
		const startTime = performance.now();

		const tick = (now) => {
			const progress = Math.min((now - startTime) / duration, 1);
			const eased = 1 - Math.pow(1 - progress, 3);
			setCount(Math.round(start + (end - start) * eased));
			if (progress < 1) requestAnimationFrame(tick);
		};
		requestAnimationFrame(tick);
	}, [inView, value]);

	return (
		<Text ref={ref} fontSize='3xl' fontWeight='extrabold' mb={0} bgGradient='linear(to-r, cyan.200, blue.200)' bgClip='text'>
			{count}
			{suffix}
		</Text>
	);
};

const HeroCampaignFloatCard = () => {
	const ref = useRef(null);
	const inView = useInView(ref, { once: true });
	const [percent, setPercent] = useState(0);

	useEffect(() => {
		if (!inView) return undefined;
		const duration = 1600;
		const startTime = performance.now();
		let frameId;

		const tick = (now) => {
			const progress = Math.min((now - startTime) / duration, 1);
			const eased = 1 - Math.pow(1 - progress, 3);
			setPercent(Math.round(127 * eased));
			if (progress < 1) frameId = requestAnimationFrame(tick);
		};

		frameId = requestAnimationFrame(tick);
		return () => cancelAnimationFrame(frameId);
	}, [inView]);

	return (
		<MotionBox
			ref={ref}
			position='absolute'
			top='-60px'
			right='-12px'
			bg='white'
			rounded='xl'
			p={4}
			boxShadow='xl'
			display={{ base: 'none', md: 'block' }}
			initial={{ opacity: 0, x: -28, y: 16, rotate: -2 }}
			animate={{
				opacity: 1,
				x: 0,
				y: [0, -9, 0],
				rotate: [-2, 0, -2],
			}}
			transition={{
				opacity: { duration: 0.55, delay: 0.4 },
				x: { duration: 0.6, delay: 0.4, ease: [0.22, 1, 0.36, 1] },
				y: { duration: 4.5, repeat: Infinity, ease: 'easeInOut', delay: 1.1 },
				rotate: { duration: 4.5, repeat: Infinity, ease: 'easeInOut', delay: 1.1 },
			}}>
			<Text fontSize='xs' color='gray.500' fontWeight='bold' textTransform='uppercase' mb={1}>
				Campaigns sent
			</Text>
			<MotionText
				fontSize='2xl'
				fontWeight='extrabold'
				color='gray.900'
				mb={1}
				animate={{ scale: [1, 1.06, 1] }}
				transition={{ duration: 2.2, repeat: Infinity, ease: 'easeInOut', delay: 1.8 }}>
				+{percent}%
			</MotionText>
			<HStack spacing={1} mb={2} align='flex-end' h='28px'>
				{[35, 55, 45, 80, 60, 95, 70].map((h, i) => (
					<MotionBox
						key={i}
						flex={1}
						rounded='sm'
						bg='cyan.400'
						initial={{ height: 0 }}
						animate={{ height: `${h}%` }}
						transition={{
							duration: 0.5,
							delay: 0.9 + i * 0.08,
							ease: [0.22, 1, 0.36, 1],
						}}
					/>
				))}
			</HStack>
			<MotionText
				fontSize='xs'
				color='green.500'
				mb={0}
				initial={{ opacity: 0 }}
				animate={{ opacity: [0.7, 1, 0.7] }}
				transition={{ duration: 2.5, repeat: Infinity, ease: 'easeInOut', delay: 2 }}>
				vs. last quarter
			</MotionText>
		</MotionBox>
	);
};

const HeroDeliveryFloatCard = () => (
	<MotionBox
		position='absolute'
		bottom='-16px'
		right='-8px'
		bg='gray.900'
		border='1px solid'
		borderColor='cyan.500'
		rounded='xl'
		px={4}
		py={3}
		boxShadow='2xl'
		display={{ base: 'none', md: 'block' }}
		initial={{ opacity: 0, x: 28, y: -12 }}
		animate={{
			opacity: 1,
			x: 0,
			y: [0, 8, 0],
			boxShadow: [
				'0 20px 40px rgba(0,0,0,0.45)',
				'0 22px 50px rgba(34,211,238,0.35)',
				'0 20px 40px rgba(0,0,0,0.45)',
			],
			borderColor: ['#06b6d4', '#67e8f9', '#06b6d4'],
		}}
		transition={{
			opacity: { duration: 0.55, delay: 0.55 },
			x: { duration: 0.6, delay: 0.55, ease: [0.22, 1, 0.36, 1] },
			y: { duration: 3.8, repeat: Infinity, ease: 'easeInOut', delay: 1.3 },
			boxShadow: { duration: 3.8, repeat: Infinity, ease: 'easeInOut', delay: 1.3 },
			borderColor: { duration: 3.8, repeat: Infinity, ease: 'easeInOut', delay: 1.3 },
		}}>
		<HStack spacing={3}>
			<MotionBox
				w={2.5}
				h={2.5}
				bg='green.400'
				borderRadius='full'
				animate={{
					scale: [1, 1.45, 1],
					opacity: [1, 0.65, 1],
					boxShadow: [
						'0 0 8px #48BB78',
						'0 0 18px #48BB78',
						'0 0 8px #48BB78',
					],
				}}
				transition={{ duration: 1.6, repeat: Infinity, ease: 'easeInOut' }}
			/>
			<Text color='white' fontSize='sm' fontWeight='medium' mb={0}>
				Live delivery tracking
			</Text>
		</HStack>
	</MotionBox>
);

const SectionLabel = ({ children }) => (
	<Badge
		colorScheme='cyan'
		variant='subtle'
		px={3}
		py={1}
		rounded='full'
		fontSize='xs'
		textTransform='uppercase'
		letterSpacing='wider'
		fontWeight='bold'>
		{children}
	</Badge>
);

export default function HomePage() {
	const navigate = useNavigate();
	const { isOpen: mobileNavOpen, onOpen: openMobileNav, onClose: closeMobileNav } =
		useDisclosure();
	const [hasScrolled, setHasScrolled] = useState(false);
	const [activePillar, setActivePillar] = useState(PLATFORM_PILLARS[0].id);
	const [testimonialIdx, setTestimonialIdx] = useState(0);
	const [activeSection, setActiveSection] = useState('');
	const manualNavRef = useRef(null);
	const heroRef = useRef(null);
	const heroMouseX = useSpring(0, { stiffness: 100, damping: 22 });
	const heroMouseY = useSpring(0, { stiffness: 100, damping: 22 });
	const textColor = useColorModeValue('gray.600', 'gray.300');
	const cardBg = useColorModeValue('white', 'gray.800');
	const accentColor = 'cyan.400';

	const { scrollYProgress } = useScroll();
	const heroParallax = useTransform(scrollYProgress, [0, 0.35], [0, 80]);

	const activePillarData =
		PLATFORM_PILLARS.find((p) => p.id === activePillar) || PLATFORM_PILLARS[0];

	useEffect(() => {
		const onScroll = () => setHasScrolled(window.scrollY > 24);
		onScroll();
		window.addEventListener('scroll', onScroll);
		return () => window.removeEventListener('scroll', onScroll);
	}, []);

	useEffect(() => {
		const timer = setInterval(() => {
			setTestimonialIdx((i) => (i + 1) % TESTIMONIALS.length);
		}, 7000);
		return () => clearInterval(timer);
	}, []);

	useEffect(() => {
		const sectionIds = NAV_LINKS.map((link) => link.href.replace('#', ''));
		const HEADER_OFFSET = 110;

		const getSectionTop = (el) => el.getBoundingClientRect().top + window.scrollY;

		const resolveActiveSection = () => {
			if (manualNavRef.current) {
				const target = document.getElementById(manualNavRef.current);
				if (target) {
					const rect = target.getBoundingClientRect();
					const inView = rect.top <= HEADER_OFFSET + 40 && rect.bottom > HEADER_OFFSET;
					setActiveSection(manualNavRef.current);
					if (inView) {
						manualNavRef.current = null;
					}
					return;
				}
				manualNavRef.current = null;
			}

			const scrollY = window.scrollY + HEADER_OFFSET;
			let current = sectionIds[0] || '';

			for (const id of sectionIds) {
				const el = document.getElementById(id);
				if (el && getSectionTop(el) <= scrollY) {
					current = id;
				}
			}

			setActiveSection(current);
		};

		resolveActiveSection();
		window.addEventListener('scroll', resolveActiveSection, { passive: true });
		window.addEventListener('resize', resolveActiveSection);
		return () => {
			window.removeEventListener('scroll', resolveActiveSection);
			window.removeEventListener('resize', resolveActiveSection);
		};
	}, []);

	const goSignup = () => navigate('/signup');
	const goAuth = () => navigate('/auth');

	const scrollTo = (href) => {
		const sectionId = href.replace('#', '');
		manualNavRef.current = sectionId;
		setActiveSection(sectionId);
		closeMobileNav();
		document.querySelector(href)?.scrollIntoView({ behavior: 'smooth' });
	};

	const onHeroMouseMove = (e) => {
		const rect = heroRef.current?.getBoundingClientRect();
		if (!rect) return;
		const x = (e.clientX - rect.left) / rect.width - 0.5;
		const y = (e.clientY - rect.top) / rect.height - 0.5;
		heroMouseX.set(x * 18);
		heroMouseY.set(y * 12);
	};

	return (
		<Box bg='gray.50' minH='100vh' fontFamily='Inter, system-ui, sans-serif' overflowX='hidden'>
			<ScrollProgressBar />
			{/* ── Nav ── */}
			<Box
				as='header'
				position='fixed'
				top={0}
				w='full'
				zIndex={30}
				bg={hasScrolled ? 'rgba(6, 14, 36, 0.82)' : 'transparent'}
				backdropFilter={hasScrolled ? 'saturate(180%) blur(18px)' : 'none'}
				borderBottom={hasScrolled ? '1px solid' : 'none'}
				borderColor='whiteAlpha.200'
				transition='all 0.25s ease'>
				<Container maxW='container.xl' py={4}>
					<Flex justify='space-between' align='center'>
						<HStack spacing={3}>
							<Img src={OpPilotCRMLogo} maxW='44px' alt='Ops Pilot CRM' />
							<Text
								display={{ base: 'none', sm: 'block' }}
								fontWeight='bold'
								color='white'
								fontSize='lg'
								mb={0}>
								Ops Pilot
							</Text>
						</HStack>
						<HStack spacing={8} display={{ base: 'none', md: 'flex' }}>
							{NAV_LINKS.map((link) => (
								<NavSectionLink
									key={link.href}
									href={link.href}
									label={link.label}
									accentColor={accentColor}
									isActive={activeSection === link.href.replace('#', '')}
									onNavigate={scrollTo}
								/>
							))}
						</HStack>
						<HStack spacing={3}>
							<Button
								variant='ghost'
								color='white'
								size='sm'
								display={{ base: 'none', sm: 'flex' }}
								_hover={{ bg: 'whiteAlpha.200' }}
								onClick={goAuth}>
								Log in
							</Button>
							<Button
								size='sm'
								bg={accentColor}
								color='gray.900'
								rounded='full'
								px={5}
								display={{ base: 'none', sm: 'flex' }}
								_hover={{ bg: 'cyan.300', transform: 'translateY(-1px)' }}
								transition='all 0.2s'
								onClick={goSignup}
								rightIcon={<FiArrowRight />}
								sx={primaryCtaSx}>
								Choose your plan
							</Button>
							<IconButton
								display={{ base: 'flex', md: 'none' }}
								aria-label='Open menu'
								icon={<FiMenu />}
								variant='ghost'
								color='white'
								size='lg'
								onClick={openMobileNav}
							/>
						</HStack>
					</Flex>
				</Container>
			</Box>

			<Drawer isOpen={mobileNavOpen} placement='right' onClose={closeMobileNav} size='xs'>
				<DrawerOverlay backdropFilter='blur(4px)' />
				<DrawerContent bg='gray.900' color='white'>
					<DrawerCloseButton color='white' />
					<DrawerHeader borderBottomWidth='1px' borderColor='whiteAlpha.200'>
						<HStack spacing={2}>
							<Img src={OpPilotCRMLogo} maxW='32px' alt='' />
							<Text mb={0} fontWeight='bold'>
								Ops Pilot
							</Text>
						</HStack>
					</DrawerHeader>
					<DrawerBody py={6}>
						<VStack align='stretch' spacing={1}>
							{NAV_LINKS.map((link) => (
								<Button
									key={link.href}
									variant='ghost'
									justifyContent='flex-start'
									color='whiteAlpha.900'
									size='lg'
									_hover={{ bg: 'whiteAlpha.100', color: 'cyan.300' }}
									onClick={() => scrollTo(link.href)}>
									{link.label}
								</Button>
							))}
							<Button
								variant='ghost'
								justifyContent='flex-start'
								color='whiteAlpha.900'
								size='lg'
								onClick={() => {
									closeMobileNav();
									goAuth();
								}}>
								Log in
							</Button>
							<Divider borderColor='whiteAlpha.200' my={4} />
							<Button
								colorScheme='cyan'
								rounded='full'
								size='lg'
								rightIcon={<FiArrowRight />}
								onClick={() => {
									closeMobileNav();
									goSignup();
								}}>
								Choose your plan
							</Button>
						</VStack>
					</DrawerBody>
				</DrawerContent>
			</Drawer>

			{/* ── Hero ── */}
			<Box
				ref={heroRef}
				onMouseMove={onHeroMouseMove}
				position='relative'
				bg={HERO_BG_BASE}
				pt={{ base: 28, md: 36 }}
				pb={{ base: 16, md: 24 }}
				overflow='hidden'>
				<AuroraBackground />
				<FloatingParticles />
				<FilmGrain />

				<Container maxW='container.xl' position='relative' zIndex={3}>
					<SimpleGrid columns={{ base: 1, lg: 2 }} spacing={12} alignItems='center'>
						<VStack align={{ base: 'center', lg: 'start' }} spacing={6} textAlign={{ base: 'center', lg: 'left' }}>
							<MotionBox
								initial={{ opacity: 0, scale: 0.9 }}
								animate={{ opacity: 1, scale: 1 }}
								transition={{ duration: 0.5 }}>
								<VStack spacing={3} align={{ base: 'center', lg: 'start' }}>
									<Badge
										colorScheme='cyan'
										variant='subtle'
										px={4}
										py={1.5}
										rounded='full'
										fontSize='sm'
										textTransform='none'
										bg='whiteAlpha.150'
										color='cyan.200'>
										Ops Pilot CRM for agencies & freelancers
									</Badge>
									<MoneyBackGuaranteeBadge />
								</VStack>
							</MotionBox>

							<MotionHeading
								as='h1'
								fontSize={{ base: '2rem', md: '3rem', lg: '4rem' }}
								color='white'
								fontWeight='extrabold'
								lineHeight='1.05'
								initial={{ opacity: 0, y: 24 }}
								animate={{ opacity: 1, y: 0 }}
								transition={{ duration: 0.6, delay: 0.1 }}>
								Ditch multiple apps with<br/>{' '}
								<RotatingWord words={HERO_ROTATING_WORDS} /> <br /> from one cockpit.
							</MotionHeading>

							<MotionText
								fontSize={{ base: 'sm', md: 'md' }}
								color='whiteAlpha.800'
								maxW='xl'
								initial={{ opacity: 0, y: 20 }}
								animate={{ opacity: 1, y: 0 }}
								transition={{ duration: 0.6, delay: 0.2 }}>
								The unified operating system for your business.
								Ops Pilot integrates email marketing, CRM, invoicing, and project management with an AI-powered SEO content engine (Blogsmith) and professional branded portals.
							</MotionText>

							<MotionFlex
								gap={4}
								pt={2}
								flexWrap='wrap'
								justify={{ base: 'center', lg: 'flex-start' }}
								initial={{ opacity: 0, y: 16 }}
								animate={{ opacity: 1, y: 0 }}
								transition={{ duration: 0.6, delay: 0.3 }}>
								<Button
									size='lg'
									bg={accentColor}
									color='gray.900'
									rounded='full'
									px={8}
									h={14}
									_hover={{ bg: 'cyan.300', boxShadow: '0 8px 30px rgba(34,211,238,0.35)' }}
									onClick={goSignup}
									rightIcon={<FiArrowRight />}
									sx={primaryCtaSx}>
									Lets's try it out
								</Button>
								<Button
									size='lg'
									variant='outline'
									color='white'
									borderColor='whiteAlpha.400'
									rounded='full'
									px={8}
									h={14}
									leftIcon={<FiPlay />}
									_hover={{ bg: 'whiteAlpha.150' }}
									onClick={() =>
										document.getElementById('blogsmith')?.scrollIntoView({ behavior: 'smooth' })
									}>
									See Blogsmith in action
								</Button>
							</MotionFlex>

							<HStack spacing={6} pt={4} flexWrap='wrap' justify={{ base: 'center', lg: 'flex-start' }}>
								{HERO_TRUST_POINTS.map((item) => (
									<HStack
										key={item.text}
										spacing={2}
										bg={item.highlight ? 'whiteAlpha.150' : 'transparent'}
										px={item.highlight ? 3 : 0}
										py={item.highlight ? 1.5 : 0}
										rounded='full'
										border={item.highlight ? '1px solid' : 'none'}
										borderColor='green.300'>
										<Icon
											as={FiCheckCircle}
											color={item.highlight ? 'green.300' : 'cyan.300'}
										/>
										<Text
											color={item.highlight ? 'green.100' : 'whiteAlpha.700'}
											fontSize='sm'
											fontWeight={item.highlight ? 'semibold' : 'normal'}
											mb={0}>
											{item.text}
										</Text>
									</HStack>
								))}
							</HStack>
						</VStack>

						<MotionBox style={{ y: heroParallax }} position='relative'>
							<MotionBox style={{ x: heroMouseX, y: heroMouseY }} position='relative'>
								<HomePageHeroPreview />
								<HeroCampaignFloatCard />
								<HeroDeliveryFloatCard />
							</MotionBox>
						</MotionBox>
					</SimpleGrid>

					{/* Hero stats strip */}
					<SimpleGrid columns={{ base: 2, md: 4 }} spacing={6} mt={16} pt={8} borderTop='1px solid' borderColor='whiteAlpha.150'>
						{HERO_STATS.map((stat, i) => (
							<VStack key={stat.label} spacing={1}>
								<AnimatedCounter value={stat.value} suffix={stat.suffix} />
								<Text color='whiteAlpha.600' fontSize='sm' textAlign='center' mb={0}>
									{stat.label}
								</Text>
							</VStack>
						))}
					</SimpleGrid>
				</Container>
			</Box>

			{/* <SectionCurve fill='red' /> */}

			<MoneyBackGuaranteeStrip />

			{/* ── Trust strip ── */}
			<Box py={10} bg='white' borderBottom='1px solid' borderColor='gray.100' overflow='hidden'>
				<Text
					textAlign='center'
					fontSize='xs'
					fontWeight='bold'
					textTransform='uppercase'
					letterSpacing='wider'
					color='gray.400'
					mb={6}>
					Trusted by growing agencies & freelancers
				</Text>
				<TrustLogoMarquee />
			</Box>

			{/* I am not sure if we need this */}
			{/* <StackComparisonStrip /> */}

			{/* ── Interactive platform pillars ── */}
			<Box
				py={{ base: 16, md: 24 }}
				bg='gray.50'
				id='platform'
				position='relative'
				overflow='hidden'>
				<MotionBox
					position='absolute'
					top='20%'
					right='-10%'
					w='500px'
					h='500px'
					borderRadius='full'
					bg={PILLAR_GLOW[activePillar] || PILLAR_GLOW.email}
					filter='blur(80px)'
					animate={{ opacity: [0.5, 0.9, 0.5], scale: [0.95, 1.05, 0.95] }}
					transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }}
					pointerEvents='none'
					aria-hidden
				/>
				<Container maxW='container.xl' position='relative' zIndex={1}>
					<Reveal>
						<VStack spacing={4} textAlign='center' mb={12}>
							<SectionLabel>Full-stack operations</SectionLabel>
							<Heading size='2xl' color='gray.900' fontWeight='extrabold'>
								Everything your agency runs on — connected
							</Heading>
							<Text color={textColor} fontSize='lg' maxW='2xl'>
								One subscription replaces your email, CRM, project, billing, and content tools.
							</Text>
						</VStack>
					</Reveal>

					<SimpleGrid columns={{ base: 1, lg: 12 }} spacing={8}>
						<VStack align='stretch' spacing={2} gridColumn={{ lg: 'span 4' }}>
							{PLATFORM_PILLARS.map((pillar) => {
								const isActive = pillar.id === activePillar;
								return (
									<MotionBox
										key={pillar.id}
										as='button'
										type='button'
										onClick={() => setActivePillar(pillar.id)}
										textAlign='left'
										w='full'
										p={4}
										rounded='xl'
										bg={isActive ? 'white' : 'transparent'}
										border='2px solid'
										borderColor={isActive ? `${pillar.accent}.400` : 'transparent'}
										boxShadow={isActive ? 'md' : 'none'}
										whileHover={{ x: 4 }}
										transition={{ type: 'spring', stiffness: 400, damping: 28 }}>
										<HStack spacing={3}>
											<Flex
												w={10}
												h={10}
												rounded='lg'
												align='center'
												justify='center'
												bg={isActive ? `${pillar.accent}.50` : 'gray.100'}
												color={isActive ? `${pillar.accent}.600` : 'gray.500'}>
												<Icon as={pillar.icon} boxSize={5} />
											</Flex>
											<Text
												fontWeight='semibold'
												color={isActive ? 'gray.900' : 'gray.600'}
												mb={0}>
												{pillar.label}
											</Text>
										</HStack>
									</MotionBox>
								);
							})}
						</VStack>

						<Box gridColumn={{ lg: 'span 8' }} position='relative'>
							<AnimatePresence mode='wait'>
								<MotionBox
									key={activePillarData.id}
									initial={{ opacity: 0, x: 20, scale: 0.98 }}
									animate={{ opacity: 1, x: 0, scale: 1 }}
									exit={{ opacity: 0, x: -20, scale: 0.98 }}
									transition={{ duration: 0.35 }}
									bg={cardBg}
									rounded='2xl'
									p={{ base: 6, md: 10 }}
									boxShadow='lg'
									border='1px solid'
									borderColor='gray.100'
									position='relative'
									overflow='hidden'>
									<Box
										position='absolute'
										top={0}
										left={0}
										right={0}
										h='4px'
										bgGradient={activePillarData.gradient}
									/>
									<Badge
										colorScheme={activePillarData.accent}
										mb={4}
										rounded='full'
										textTransform='none'>
										{activePillarData.label}
									</Badge>
									<Heading size='lg' mb={3} color='gray.900'>
										{activePillarData.headline}
									</Heading>
									<Text color={textColor} fontSize='lg' mb={6} lineHeight='tall'>
										{activePillarData.description}
									</Text>
									<VStack align='start' spacing={3} mb={6}>
										{activePillarData.bullets.map((b) => (
											<HStack key={b} align='start'>
												<Icon as={FiCheckCircle} color={`${activePillarData.accent}.500`} mt={1} />
												<Text color='gray.700' mb={0}>
													{b}
												</Text>
											</HStack>
										))}
									</VStack>
									<Button
										variant='link'
										colorScheme={activePillarData.accent}
										rightIcon={<FiArrowRight />}
										onClick={goSignup}>
										Choose your plan
									</Button>
								</MotionBox>
							</AnimatePresence>
						</Box>
					</SimpleGrid>

					<SimpleGrid columns={{ base: 1, sm: 2, lg: 4 }} spacing={4} mt={10}>
						{CAPABILITY_GRID.map((cap, i) => (
							<Reveal key={cap.title} delay={i * 0.05}>
								<HStack
									align='start'
									p={4}
									rounded='xl'
									bg={cardBg}
									border='1px solid'
									borderColor='gray.100'
									h='full'
									transition='all 0.25s'
									_hover={{ transform: 'translateY(-2px)', boxShadow: 'sm' }}>
									<Flex
										w={9}
										h={9}
										rounded='lg'
										bg='blue.50'
										color='blue.600'
										align='center'
										justify='center'
										flexShrink={0}>
										<Icon as={cap.icon} boxSize={4} />
									</Flex>
									<Box>
										<Text fontWeight='semibold' fontSize='sm' color='gray.900' mb={0}>
											{cap.title}
										</Text>
										<Text color={textColor} fontSize='xs' mb={0}>
											{cap.desc}
										</Text>
									</Box>
								</HStack>
							</Reveal>
						))}
					</SimpleGrid>
				</Container>
			</Box>

			<HomePageBlogsmithSpotlight onCta={goSignup} />

			{/* ── Workflow ── */}
			<Box py={{ base: 16, md: 24 }} bg='#f7fafc' id='workflow'>
				<Container maxW='container.xl'>
					<Reveal>
						<VStack spacing={4} textAlign='center' mb={14}>
							<SectionLabel>How it works</SectionLabel>
							<Heading size='2xl' color='gray.900' fontWeight='extrabold'>
								From signup to shipped — in three moves
							</Heading>
							<Text color={textColor} fontSize='lg' maxW='xl'>
								A clear path designed for agencies and freelancers who need results, not
								another tool to configure.
							</Text>
						</VStack>
					</Reveal>
					<Box position='relative' display={{ base: 'none', md: 'block' }} mb={6}>
						<Box position='absolute' top='50%' left='8%' right='8%' h='2px' bg='gray.200' zIndex={0} />
						<MotionBox
							position='absolute'
							top='50%'
							left='8%'
							right='8%'
							h='2px'
							bgGradient='linear(to-r, cyan.300, blue.400, purple.400)'
							zIndex={1}
							initial={{ scaleX: 0 }}
							whileInView={{ scaleX: 1 }}
							viewport={{ once: true }}
							transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
							style={{ transformOrigin: '0% 50%' }}
						/>
					</Box>
					<SimpleGrid columns={{ base: 1, md: 3 }} spacing={6} position='relative' zIndex={2}>
						{WORKFLOW_STEPS.map((step, i) => (
							<Reveal key={step.step} delay={i * 0.1}>
								<Box
									p={6}
									rounded='2xl'
									bg='gray.50'
									border='1px solid'
									borderColor='gray.100'
									h='full'
									position='relative'
									overflow='hidden'
									_hover={{ borderColor: 'cyan.300', transform: 'translateY(-4px)', boxShadow: 'md' }}
									transition='all 0.3s cubic-bezier(0.22, 1, 0.36, 1)'>
									<Text
										fontSize='4xl'
										fontWeight='black'
										color='cyan.100'
										position='absolute'
										top={2}
										right={4}
										mb={0}
										lineHeight={1}>
										{step.step}
									</Text>
									<Heading size='md' color='gray.900' mb={3} pr={12}>
										{step.title}
									</Heading>
									<Text color={textColor} fontSize='sm' mb={0}>
										{step.desc}
									</Text>
								</Box>
							</Reveal>
						))}
					</SimpleGrid>
				</Container>
			</Box>

			<SectionCurve fill='#0f172a' />

			{/* ── Testimonials carousel ── */}
			<Box py={{ base: 16, md: 24 }} bg='#0f172a' color='white' id='testimonials' position='relative' overflow='hidden'>
				<FilmGrain opacity={0.03} />
				<Container maxW='container.lg' position='relative' zIndex={1}>
					<Reveal>
						<VStack spacing={4} textAlign='center' mb={10}>
							<Heading size='2xl' fontWeight='extrabold' color='white'>
								Teams that switched — and stayed
							</Heading>
							<Text color='whiteAlpha.700' fontSize='lg'>
								Real workflows. Real clients. Less chaos.
							</Text>
						</VStack>
					</Reveal>

					<Box position='relative'>
						<AnimatePresence mode='wait'>
							<MotionBox
								key={testimonialIdx}
								initial={{ opacity: 0, y: 20, scale: 0.97, filter: 'blur(6px)' }}
								animate={{ opacity: 1, y: 0, scale: 1, filter: 'blur(0px)' }}
								exit={{ opacity: 0, y: -16, scale: 0.98, filter: 'blur(4px)' }}
								transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}>
								<Card
									bg='whiteAlpha.100'
									border='1px solid'
									borderColor='whiteAlpha.200'
									rounded='2xl'
									p={{ base: 6, md: 10 }}
									color='white'>
									<CardBody p={0}>
										<HStack spacing={1} color='yellow.400' mb={4}>
											{[...Array(5)].map((_, idx) => (
												<Icon key={idx} as={FiStar} fill='currentColor' />
											))}
										</HStack>
										<Badge
											colorScheme='cyan'
											variant='solid'
											mb={4}
											rounded='full'
											px={3}
											py={1}
											fontSize='xs'
											textTransform='none'>
											{TESTIMONIALS[testimonialIdx].metric}
										</Badge>
										<Text
											fontSize={{ base: 'lg', md: 'xl' }}
											lineHeight='tall'
											fontWeight='medium'
											mb={8}
											color='gray.100'>
											&ldquo;{TESTIMONIALS[testimonialIdx].text}&rdquo;
										</Text>
										<HStack spacing={4}>
											<Avatar src={TESTIMONIALS[testimonialIdx].avatar} size='lg' />
											<Box>
												<Text fontWeight='bold' fontSize='lg' mb={0} color='white'>
													{TESTIMONIALS[testimonialIdx].name}
												</Text>
												<Text color='cyan.300' fontSize='sm' mb={0}>
													{TESTIMONIALS[testimonialIdx].role}
												</Text>
											</Box>
										</HStack>
									</CardBody>
								</Card>
							</MotionBox>
						</AnimatePresence>

						<HStack justify='center' mt={6} spacing={4}>
							<Button
								size='sm'
								variant='ghost'
								color='white'
								rounded='full'
								onClick={() =>
									setTestimonialIdx((i) => (i - 1 + TESTIMONIALS.length) % TESTIMONIALS.length)
								}
								aria-label='Previous testimonial'>
								<FiChevronLeft />
							</Button>
							<HStack spacing={2}>
								{TESTIMONIALS.map((_, i) => (
									<Box
										key={i}
										w={i === testimonialIdx ? 6 : 2}
										h={2}
										rounded='full'
										bg={i === testimonialIdx ? 'cyan.400' : 'whiteAlpha.400'}
										transition='all 0.3s'
										cursor='pointer'
										onClick={() => setTestimonialIdx(i)}
									/>
								))}
							</HStack>
							<Button
								size='sm'
								variant='ghost'
								color='white'
								rounded='full'
								onClick={() => setTestimonialIdx((i) => (i + 1) % TESTIMONIALS.length)}
								aria-label='Next testimonial'>
								<FiChevronRight />
							</Button>
						</HStack>
					</Box>
				</Container>
			</Box>

			<SectionCurve fill='#0f172a' flip />

			<HomePagePricingSection goSignup={goSignup} primaryCtaSx={primaryCtaSx} />


			<HomePageBlogsmithFaq />

			{/* ── Final CTA ── */}
			<Box
				py={{ base: 16, md: 20 }}
				bg='#eee'
				position='relative'
				overflow='hidden'>
				<Container maxW='container.md' position='relative' zIndex={1} textAlign='center'>
						<Box mb={8} display='flex' justifyContent='center'>
							<MoneyBackGuaranteeBadge />
						</Box>
					<Reveal>
						<Heading size='2xl' color='gray.900' mb={4} fontWeight='extrabold'>
							Start growing without adding tools or hires
						</Heading>
						<Text color='gray.600' fontSize='lg' mb={4} py={4}>
							Join agencies and freelancers who run campaigns, clients, projects, and
							billing from one intelligent workspace.
						</Text>
						<HStack justify='center' spacing={4} flexWrap='wrap'>
							<Button
								size='lg'
								bg='cyan.400'
								color='gray.900'
								rounded='full'
								px={10}
								h={14}
								_hover={{ bg: 'cyan.300', transform: 'scale(1.02)' }}
								onClick={goSignup}
								rightIcon={<FiArrowRight />}
								sx={primaryCtaSx}>
								Choose your plan
							</Button>
							<Button
								size='lg'
								variant='outline'
								color='gray.800'
								borderColor='gray.300'
								rounded='full'
								px={8}
								h={14}
								_hover={{ bg: 'gray.100' }}
								onClick={goAuth}>
								I already have an account
							</Button>
						</HStack>
						<Text fontSize='sm' color='gray.500' mt={6} mb={0}>
							{MONEY_BACK_GUARANTEE.title} · Cancel anytime · Secure Stripe checkout
						</Text>
					</Reveal>
				</Container>
			</Box>

			{/* ── Footer ── */}
			<Footer />
		</Box>
	);
}
